import { useEffect, useState } from "react";
import { toast } from "sonner";
import { EyeOff, Loader2, Undo2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { api } from "../lib/api";

// Bulk move riders to «بدون مشرف» (hidden from every report) + list to bring them back.
export const HiddenRidersBar = ({ platform, sel, clearSel, onChanged, canEdit }) => {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [pick, setPick] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const load = () => api.ridersHidden(platform).then(setList).catch(() => setList([]));
  useEffect(() => { load(); }, [platform]); // eslint-disable-line react-hooks/exhaustive-deps
  const run = async (ids, hidden) => {
    setBusy(true);
    try {
      const r = await api.ridersHide(platform, ids, hidden);
      toast.success(hidden ? `انتقل ${r.riders} مندوب إلى «بدون مشرف» واختفوا من التقارير` : `رجع ${r.riders} مندوب للتقارير`);
      clearSel?.(); setPick(new Set()); await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر التنفيذ"); } finally { setBusy(false); }
  };
  if (!canEdit) return null;
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="hidden-riders-bar">
      {sel.size > 0 && (
        <Button size="sm" variant="outline" disabled={busy} onClick={() => window.confirm(`نقل ${sel.size} مندوب إلى «بدون مشرف»؟ ما يظهرون في أي تقرير لين ترجّعهم.`) && run([...sel], true)}
          className="gap-1.5 border-rose-200 text-rose-700 hover:bg-rose-50" data-testid="riders-move-hidden">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <EyeOff className="h-4 w-4" />} نقل المحدد إلى بدون مشرف ({sel.size})
        </Button>)}
      <Button size="sm" variant="ghost" onClick={() => setOpen(true)} className="gap-1.5 text-slate-600" data-testid="riders-hidden-open">
        <EyeOff className="h-4 w-4" /> بدون مشرف ({list.length})
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent dir="rtl" className="max-w-lg font-body" data-testid="riders-hidden-dialog">
          <DialogHeader className="text-right"><DialogTitle className="font-heading">مناديب بدون مشرف</DialogTitle>
            <DialogDescription>هالمناديب مخفيين من كل التقارير والإحصائيات (الرواتب ما تتأثر). اختر اللي تبي ترجّعهم.</DialogDescription></DialogHeader>
          <div className="max-h-[50vh] space-y-1 overflow-y-auto">
            {list.length === 0 && <p className="py-6 text-center text-sm text-slate-400">ما فيه مناديب بدون مشرف</p>}
            {list.map((r) => (
              <label key={r.rider_id} className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-100 px-3 py-2 text-sm hover:bg-slate-50" data-testid={`hidden-rider-${r.rider_id}`}>
                <input type="checkbox" checked={pick.has(r.rider_id)} onChange={() => { const n = new Set(pick); n.has(r.rider_id) ? n.delete(r.rider_id) : n.add(r.rider_id); setPick(n); }} />
                <span className="flex-1 font-semibold">{r.rider_name || r.rider_id}<span className="mr-2 font-mono-nums text-[11px] font-normal text-slate-400">#{r.rider_id}</span></span>
                <span className="text-[11px] text-slate-400">{r.by}</span>
              </label>))}
          </div>
          {list.length > 0 && <div className="flex justify-between gap-2">
            <Button size="sm" variant="ghost" onClick={() => setPick(pick.size === list.length ? new Set() : new Set(list.map((r) => r.rider_id)))} data-testid="hidden-select-all">{pick.size === list.length ? "إلغاء التحديد" : "تحديد الكل"}</Button>
            <Button size="sm" disabled={!pick.size || busy} onClick={() => run([...pick], false)} className="gap-1" data-testid="hidden-restore">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Undo2 className="h-4 w-4" />} إرجاع للتقارير ({pick.size})</Button>
          </div>}
        </DialogContent>
      </Dialog>
    </div>
  );
};
